import { getApiBaseUrl } from '../lib/getApiUrl';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../lib/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (name: string, email: string, password: string, role?: string) => Promise<boolean>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = getApiBaseUrl();

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    console.log('AuthContext: Checking for stored token:', storedToken ? 'Found' : 'Not found');
    if (storedToken) {
      setToken(storedToken);
      fetchUserProfile(storedToken);
    } else {
      console.log('AuthContext: No stored token, setting loading to false');
      setIsLoading(false);
    }
  }, []);

  // Multi-tab session synchronization via storage events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'authToken') {
        if (e.newValue === null) {
          // Token removed in another tab - logout this tab
          console.log('[Auth] Token removed in another tab, logging out');
          setUser(null);
          setToken(null);
        } else if (e.newValue !== token) {
          // Token changed in another tab - update this tab
          console.log('[Auth] Token changed in another tab, updating');
          setToken(e.newValue);
          // Re-fetch user profile with new token
          fetchUserProfile(e.newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token]);

  const fetchUserProfile = async (authToken: string) => {
    try {
      console.log('AuthContext: Fetching user profile...');
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('AuthContext: User profile fetched successfully:', userData);
        
        // Handle the response structure from backend
        if (userData.success && userData.data && userData.data.user) {
          setUser(userData.data.user);
        } else if (userData.success && userData.data) {
          setUser(userData.data);
        } else if (userData.id) {
          // Handle direct user object
          setUser(userData);
        } else {
          console.error('Unexpected user data structure:', userData);
          localStorage.removeItem('token');
          setToken(null);
        }
      } else {
        console.log('AuthContext: Token is invalid, clearing it');
        // Token is invalid, clear it
        localStorage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('AuthContext: Error fetching user profile:', error);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        // Token/response logging removed for security

        // Handle the response structure from backend
        if (data.success && data.data && data.data.user && data.data.token) {
          setUser(data.data.user);
          setToken(data.data.token);
          localStorage.setItem('token', data.data.token);
          localStorage.setItem('authToken', data.data.token);
          return { success: true };
        } else if (data.user && data.token) {
          // Handle legacy response structure
          setUser(data.user);
          setToken(data.token);
          localStorage.setItem('token', data.token);
          localStorage.setItem('authToken', data.token);
          return { success: true };
        } else {
          console.error('Unexpected response structure:', data);
          return { success: false, error: 'Unexpected server response. Please try again.' };
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Login failed:', errorData);
        const message = errorData.message || errorData.error || 'Invalid email or password';
        return { success: false, error: message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Unable to connect to server. Please check your internet connection.' };
    }
  };

  const register = async (name: string, email: string, password: string, role: string = 'EXECUTIVE'): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password, role }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setToken(data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('authToken', data.token);
        return true;
      } else {
        const errorData = await response.json();
        console.error('Registration failed:', errorData.error);
        return false;
      }
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
  };

  const updateProfile = async (data: Partial<User>): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser.user);
        return true;
      } else {
        const errorData = await response.json();
        console.error('Profile update failed:', errorData.error);
        return false;
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 
